import type { JSX } from 'react';

import { Wrench } from 'lucide-react';

import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';

import { useGetCaseConversationQuery } from '../../api/cases';
import { caseClientName, type AgentCase } from '../../model/case';
import type { ConversationImportTurn } from '../../model/conversationImport';

interface CaseConversationDialogProps {
  agentCase: AgentCase | null;
  onClose: () => void;
}

const formatToolResult = (toolResult: unknown): string =>
  typeof toolResult === 'string' ? toolResult : JSON.stringify(toolResult);

const TurnBubble = ({ turn }: { turn: ConversationImportTurn }): JSX.Element => {
  if (turn.role === 'tool') {
    return (
      <div className="flex justify-end">
        <div className="flex max-w-[85%] flex-col gap-1 rounded-lg bg-brand-vivid px-3 py-2 opacity-80">
          <span className="flex items-center gap-1.5 text-[0.7rem] font-medium text-white/80">
            <Wrench className="size-3" aria-hidden />
            Tool{turn.toolName ? ` · ${turn.toolName}` : ''}
            {turn.outdated ? ' · outdated' : ''}
          </span>
          <pre className="m-0 font-mono text-xs break-words whitespace-pre-wrap text-white">{turn.content}</pre>
          {turn.toolResult !== undefined && !turn.outdated ? (
            <pre className="m-0 border-t border-white/20 pt-1 font-mono text-xs break-words whitespace-pre-wrap text-white/90">
              → {formatToolResult(turn.toolResult)}
            </pre>
          ) : null}
        </div>
      </div>
    );
  }

  const isUser = turn.role === 'user';
  return (
    <div className={cn('flex', isUser ? 'justify-start' : 'justify-end')}>
      <div
        className={cn(
          'flex max-w-[85%] flex-col gap-0.5 rounded-lg px-3 py-2',
          isUser ? 'bg-bg-surface-alt' : 'bg-brand-vivid'
        )}
      >
        <span className={cn('text-[0.7rem] font-medium', isUser ? 'text-text-muted' : 'text-white/80')}>
          {isUser ? 'Cliente' : 'Agente'}
        </span>
        <p className={cn('m-0 text-sm break-words', isUser ? 'text-text-default' : 'text-white')}>{turn.content}</p>
      </div>
    </div>
  );
};

const CaseConversationDialog = ({ agentCase, onClose }: CaseConversationDialogProps): JSX.Element => {
  const { data, isFetching, isError } = useGetCaseConversationQuery(
    { caseId: agentCase?.id ?? 0 },
    { skip: agentCase === null }
  );
  const conversation = data?.conversation;

  return (
    <Dialog open={agentCase !== null} onOpenChange={(isOpen) => (!isOpen ? onClose() : undefined)}>
      <DialogContent className="flex max-h-[85vh] flex-col sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Conversación de {agentCase ? caseClientName(agentCase.client) : ''}</DialogTitle>
          <DialogDescription>
            {agentCase
              ? `Evaluación #${agentCase.id} — la conversación completa del cliente, con PII anonimizada.`
              : ''}
          </DialogDescription>
        </DialogHeader>

        {isFetching ? <p className="m-0 py-8 text-center text-sm text-text-muted">Cargando conversación…</p> : null}

        {isError && !isFetching ? (
          <p className="m-0 py-8 text-center text-sm text-error" role="alert">
            No se pudo cargar la conversación.
          </p>
        ) : null}

        {!isFetching && !isError && conversation ? (
          <>
            {conversation.truncated ? (
              <p className="m-0 rounded-md bg-warning/10 px-3 py-2 text-xs text-warning">
                Conversación truncada: se muestran los últimos {conversation.turns.length} turnos de{' '}
                {conversation.totalMessages} mensajes.
              </p>
            ) : null}
            <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto pr-1">
              {conversation.turns.length === 0 ? (
                <p className="m-0 py-8 text-center text-sm text-text-muted">Esta conversación no tiene mensajes.</p>
              ) : (
                conversation.turns.map((turn, index) => <TurnBubble key={index} turn={turn} />)
              )}
            </div>
          </>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};

export default CaseConversationDialog;
